import { ENV } from './amplify';
import { fetchAuthSession } from 'aws-amplify/auth';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function authHeader(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const callerHeaders = (init.headers as Record<string, string> | undefined) ?? {};
  // If caller supplied an Idempotency-Key (e.g. on replay from the offline
  // queue), reuse it. Otherwise mint one for any non-idempotent verb so the
  // server can dedupe accidental retries.
  const needsIdempotency = method !== 'GET' && method !== 'HEAD';
  const idempotencyHeader: Record<string, string> =
    needsIdempotency
      ? callerHeaders['Idempotency-Key']
        ? {}
        : typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? { 'Idempotency-Key': crypto.randomUUID() }
          : {}
      : {};
  const headers = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
    ...idempotencyHeader,
    ...callerHeaders,
  };
  // Transient-failure retry. Lambda concurrency throttles surface as a 503 from
  // API Gateway before the handler ever runs; without a retry the UI renders
  // them as "no data", which reads as data loss. Safe to retry: GETs, and any
  // request carrying an Idempotency-Key (the server de-dupes replays). We do
  // NOT retry network errors — the worker app depends on those to enqueue
  // offline writes.
  const retriable = method === 'GET' || 'Idempotency-Key' in headers;
  const RETRY_STATUS = [502, 503, 504];
  let res!: Response;
  for (let attempt = 0; ; attempt += 1) {
    res = await fetch(new URL(path, ENV.apiUrl), { ...init, headers });
    if (!retriable || attempt >= 2 || !RETRY_STATUS.includes(res.status)) break;
    // 250ms, 750ms + jitter
    const backoff = 250 * 3 ** attempt + Math.random() * 150;
    await new Promise((r) => setTimeout(r, backoff));
  }
  let body: unknown = null;
  if (res.headers.get('content-type')?.includes('application/json')) {
    body = await res.json().catch(() => null);
  } else {
    body = await res.text().catch(() => '');
  }
  if (!res.ok) {
    const message =
      (typeof body === 'object' && body !== null && 'message' in body && typeof (body as { message: unknown }).message === 'string'
        ? (body as { message: string }).message
        : null) ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, body, message);
  }
  return body as T;
}
